/**
 * JS-domain DSP primitives. These generate Float32Arrays that get wrapped into
 * AudioBuffers; doing the *content* in JS and the *routing/filtering* in Web Audio
 * nodes gives precise, deterministic, sample-accurate material with cheap playback.
 */
import { noiseSlice, normalise } from './noise.js';

// re-exported so every bank can pull its DSP from one place
export { normalise, noiseSlice };

export const TAU = Math.PI * 2;

/* ---------------------------------------------------------------- envelopes */

/** exp(-t/tau) with a hard end-fade so a buffer never clicks on its last sample. */
export function applyExpDecay(out, sampleRate, tau, delaySamples = 0) {
  const n = out.length;
  for (let i = 0; i < n; i++) {
    if (i < delaySamples) { out[i] = 0; continue; }
    const t = (i - delaySamples) / sampleRate;
    out[i] *= Math.exp(-t / tau);
  }
  return fadeOut(out, Math.min(256, n >> 2));
}

/** Attack/decay envelope: linear-ish rise over `attack` s, exponential fall. */
export function applyAD(out, sampleRate, attack, tau, delaySamples = 0) {
  const n = out.length;
  const aN = Math.max(1, Math.floor(attack * sampleRate));
  for (let i = 0; i < n; i++) {
    if (i < delaySamples) { out[i] = 0; continue; }
    const k = i - delaySamples;
    const t = k / sampleRate;
    const a = k < aN ? k / aN : 1;
    out[i] *= a * a * Math.exp(-Math.max(0, t - attack) / tau);
  }
  return fadeOut(out, Math.min(256, n >> 2));
}

export function fadeIn(out, samples) {
  const n = Math.min(samples, out.length);
  for (let i = 0; i < n; i++) out[i] *= i / n;
  return out;
}

export function fadeOut(out, samples) {
  const n = Math.min(samples, out.length);
  const L = out.length;
  for (let i = 0; i < n; i++) out[L - 1 - i] *= i / n;
  return out;
}

export function scale(out, g) {
  for (let i = 0; i < out.length; i++) out[i] *= g;
  return out;
}

export function addInto(dst, src, offset = 0, gain = 1) {
  const n = Math.min(src.length, dst.length - offset);
  for (let i = 0; i < n; i++) dst[offset + i] += src[i] * gain;
  return dst;
}

/* ----------------------------------------------------------------- filters */

/** One-pole lowpass, in place. cutoff in Hz. */
export function onePoleLP(out, sampleRate, cutoff) {
  const a = Math.exp((-TAU * cutoff) / sampleRate);
  let z = 0;
  for (let i = 0; i < out.length; i++) { z = out[i] * (1 - a) + z * a; out[i] = z; }
  return out;
}

/** One-pole highpass, in place. */
export function onePoleHP(out, sampleRate, cutoff) {
  const a = Math.exp((-TAU * cutoff) / sampleRate);
  let z = 0;
  for (let i = 0; i < out.length; i++) { z = out[i] * (1 - a) + z * a; out[i] -= z; }
  return out;
}

/** Time-varying one-pole lowpass; cutoffAt(i) returns Hz. Great for filter sweeps. */
export function sweepLP(out, sampleRate, cutoffAt) {
  let z = 0;
  for (let i = 0; i < out.length; i++) {
    const fc = Math.max(20, Math.min(sampleRate * 0.45, cutoffAt(i / sampleRate)));
    const a = Math.exp((-TAU * fc) / sampleRate);
    z = out[i] * (1 - a) + z * a;
    out[i] = z;
  }
  return out;
}

/** Transposed-direct-form-II biquad, in place. */
export function biquad(out, b0, b1, b2, a1, a2) {
  let z1 = 0, z2 = 0;
  for (let i = 0; i < out.length; i++) {
    const x = out[i];
    const y = b0 * x + z1;
    z1 = b1 * x - a1 * y + z2;
    z2 = b2 * x - a2 * y;
    out[i] = y;
  }
  return out;
}

/** RBJ bandpass (constant peak gain), applied in place. */
export function bandpass(out, sampleRate, freq, Q) {
  const w0 = (TAU * freq) / sampleRate;
  const alpha = Math.sin(w0) / (2 * Q);
  const cw = Math.cos(w0);
  const a0 = 1 + alpha;
  return biquad(out, alpha / a0, 0, -alpha / a0, (-2 * cw) / a0, (1 - alpha) / a0);
}

/** RBJ lowpass. */
export function lowpass(out, sampleRate, freq, Q = Math.SQRT1_2) {
  const w0 = (TAU * Math.min(freq, sampleRate * 0.45)) / sampleRate;
  const alpha = Math.sin(w0) / (2 * Q);
  const cw = Math.cos(w0);
  const a0 = 1 + alpha;
  const b = (1 - cw) / 2;
  return biquad(out, b / a0, (1 - cw) / a0, b / a0, (-2 * cw) / a0, (1 - alpha) / a0);
}

/** RBJ highpass. */
export function highpass(out, sampleRate, freq, Q = Math.SQRT1_2) {
  const w0 = (TAU * Math.min(freq, sampleRate * 0.45)) / sampleRate;
  const alpha = Math.sin(w0) / (2 * Q);
  const cw = Math.cos(w0);
  const a0 = 1 + alpha;
  const b = (1 + cw) / 2;
  return biquad(out, b / a0, (-1 - cw) / a0, b / a0, (-2 * cw) / a0, (1 - alpha) / a0);
}

/** Soft saturation. `drive` > 1 pushes harder; output stays inside ±1. */
export function saturate(out, drive = 2) {
  const norm = Math.tanh(drive);
  for (let i = 0; i < out.length; i++) out[i] = Math.tanh(out[i] * drive) / norm;
  return out;
}

/* --------------------------------------------------------------- generators */

/**
 * Modal / resonant-body hit: a sum of exponentially decaying sinusoids.
 * This is what makes bolt carriers, casings, steel and glass sound like objects
 * rather than filtered noise.
 * @param {number[][]} modes [freqHz, amplitude, decaySeconds][]
 */
export function modal(sampleRate, seconds, modes, rng, jitter = 0.02) {
  const n = Math.floor(sampleRate * seconds);
  const out = new Float32Array(n);
  for (const [f0, amp, tau] of modes) {
    const f = f0 * (1 + (rng() * 2 - 1) * jitter);
    const phase = rng() * TAU;
    const w = (TAU * f) / sampleRate;
    const d = Math.exp(-1 / (tau * sampleRate));
    let env = amp;
    for (let i = 0; i < n; i++) {
      out[i] += env * Math.sin(w * i + phase);
      env *= d;
      if (env < 1e-6) break;
    }
  }
  return fadeOut(out, Math.min(128, n >> 2));
}

/**
 * Karplus–Strong plucked waveguide. Used for springs, sling swivels and the
 * ringing component of metal impacts.
 */
export function karplus(sampleRate, seconds, freq, damping, rng, excite = 'noise') {
  const n = Math.floor(sampleRate * seconds);
  const out = new Float32Array(n);
  const L = Math.max(2, Math.round(sampleRate / Math.max(20, freq)));
  const buf = new Float32Array(L);
  if (excite === 'noise') for (let i = 0; i < L; i++) buf[i] = rng() * 2 - 1;
  else for (let i = 0; i < L; i++) buf[i] = Math.sin((TAU * i) / L);
  const d = Math.max(0.5, Math.min(0.9999, damping));
  let idx = 0;
  let prev = 0;
  for (let i = 0; i < n; i++) {
    const cur = buf[idx];
    const y = 0.5 * (cur + prev) * d;
    prev = cur;
    buf[idx] = y;
    out[i] = cur;
    idx = (idx + 1) % L;
  }
  return fadeOut(out, Math.min(256, n >> 2));
}

/**
 * The transient. A sub-millisecond click: a raw impulse plus a very short burst of
 * high-passed noise. This is the crack that arrives before anything else.
 */
export function transientClick(sampleRate, rng, opts = {}) {
  const { seconds = 0.006, tau = 0.00045, hp = 1800, impulse = 1.0, noise = 0.85 } = opts;
  const n = Math.floor(sampleRate * seconds);
  const out = noiseSlice('white', n, rng);
  scale(out, noise);
  out[0] += impulse;
  out[1] += impulse * 0.55;
  out[2] -= impulse * 0.28;
  applyExpDecay(out, sampleRate, tau);
  highpass(out, sampleRate, hp, 0.8);
  return normalise(out, 1);
}

/**
 * Pitch-swept sine: the "thump". Gives a gunshot its 60–120 Hz body and an
 * explosion its chest hit.
 */
export function sineSweep(sampleRate, seconds, f0, f1, tau, rng, curve = 3) {
  const n = Math.floor(sampleRate * seconds);
  const out = new Float32Array(n);
  let phase = rng() * TAU;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const k = Math.min(1, t / seconds);
    const f = f1 + (f0 - f1) * Math.pow(1 - k, curve);
    phase += (TAU * f) / sampleRate;
    out[i] = Math.sin(phase) * Math.exp(-t / tau);
  }
  return fadeOut(out, Math.min(256, n >> 2));
}

/**
 * Supersonic N-wave: the ballistic crack of a round passing the listener.
 * Two opposite-polarity shock fronts a few hundred microseconds apart.
 */
export function nWave(sampleRate, rng, durationUs = 240, tail = 0.02) {
  const n = Math.floor(sampleRate * tail);
  const out = new Float32Array(n);
  const w = Math.max(2, Math.round((durationUs * 1e-6) * sampleRate));
  for (let i = 0; i < w && i < n; i++) out[i] = 1 - (2 * i) / w;
  // second, weaker front from the tail shock
  const off = Math.round(w * 1.15);
  for (let i = 0; i < w && off + i < n; i++) out[off + i] += -0.7 * (1 - (2 * i) / w);
  const nz = noiseSlice('white', n, rng);
  applyExpDecay(nz, sampleRate, 0.0025);
  addInto(out, nz, 0, 0.35);
  highpass(out, sampleRate, 350, 0.7);
  return normalise(out, 1);
}

/**
 * Doppler-swept ricochet whine: a resonant tone whose pitch rises then falls as
 * the fragment passes, with a noise component.
 */
export function ricochetWhine(sampleRate, seconds, fBase, rng) {
  const n = Math.floor(sampleRate * seconds);
  const out = new Float32Array(n);
  const spin = 30 + rng() * 90;
  const bend = 0.35 + rng() * 0.5;
  let p1 = rng() * TAU;
  let p2 = rng() * TAU;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const k = t / seconds;
    // approach → pass → recede: classic Doppler S-curve
    const dop = 1 + bend * (1 - 2 / (1 + Math.exp(-(k - 0.32) * 9)));
    const wob = 1 + 0.06 * Math.sin(TAU * spin * t);
    const f = fBase * dop * wob;
    p1 += (TAU * f) / sampleRate;
    p2 += (TAU * f * 1.5) / sampleRate;
    const env = Math.exp(-t / (seconds * 0.42)) * Math.min(1, t / 0.004);
    out[i] = (Math.sin(p1) + 0.32 * Math.sin(p2)) * env;
  }
  const nz = noiseSlice('white', n, rng);
  bandpass(nz, sampleRate, fBase * 1.6, 3);
  applyExpDecay(nz, sampleRate, seconds * 0.25);
  addInto(out, nz, 0, 0.3);
  return normalise(out, 0.9);
}

/**
 * Granular layering: scatter short grains of `src` across `seconds` with seeded
 * jitter in position, gain and (via linear resampling) pitch. Used for debris,
 * gravel footsteps and cloth.
 */
export function granular(sampleRate, seconds, src, rng, opts = {}) {
  const { grains = 24, grainMs = 18, spread = 1, pitchJitter = 0.4, gainJitter = 0.7 } = opts;
  const n = Math.floor(sampleRate * seconds);
  const out = new Float32Array(n);
  const gN = Math.max(8, Math.floor((grainMs / 1000) * sampleRate));
  for (let g = 0; g < grains; g++) {
    const at = Math.floor(Math.pow(rng(), 1 / Math.max(0.05, spread)) * (n - gN));
    const rate = 1 + (rng() * 2 - 1) * pitchJitter;
    const amp = (1 - gainJitter) + rng() * gainJitter;
    const srcOff = Math.floor(rng() * Math.max(1, src.length - gN * 2));
    for (let i = 0; i < gN; i++) {
      const si = srcOff + i * rate;
      const i0 = Math.floor(si);
      const f = si - i0;
      if (i0 + 1 >= src.length) break;
      const s = src[i0] * (1 - f) + src[i0 + 1] * f;
      const w = 0.5 - 0.5 * Math.cos((TAU * i) / gN);
      const o = at + i;
      if (o >= 0 && o < n) out[o] += s * w * amp;
    }
  }
  return out;
}

/** Mix a list of [Float32Array, gain, offsetSamples] into a new buffer of length n. */
export function mixdown(n, parts) {
  const out = new Float32Array(n);
  for (const [src, gain = 1, off = 0] of parts) addInto(out, src, off, gain);
  return out;
}

/** Two slightly decorrelated copies of a mono signal for a wide stereo image. */
export function widen(mono, sampleRate, rng, ms = 6) {
  const d = Math.max(1, Math.floor((ms / 1000) * sampleRate));
  const l = new Float32Array(mono.length);
  const r = new Float32Array(mono.length);
  l.set(mono);
  for (let i = 0; i < mono.length; i++) r[i] = mono[Math.max(0, i - d)] * 0.92;
  onePoleLP(r, sampleRate, 9000 + rng() * 3000);
  return [l, r];
}
